import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customerService } from '../services/customerService';
import { ActiveCustomer, UnpaidInvoice, AgentCustomerAnalysis, AgentInvoiceAnalysis } from '../types/organizations';
import { useUser } from '../context/UserContext';
import { TitleContext } from '../components/Topbar';
import ToastNotification from '../components/ToastNotification';

// Import components
import CustomerInfoCard from '../components/organization/CustomerInfoCard';
import CustomerAnalysisCard from '../components/organization/CustomerAnalysisCard';
import CustomerTabs from '../components/organization/CustomerTabs';
import CustomerAnalysisModal from '../components/organization/CustomerAnalysisModal';
import AnalysesModal from '../components/organization/modals/AnalysesModal';
import InvoiceCard from '../components/organization/InvoiceCard';
import CustomerAnalysisItemCard from '../components/organization/CustomerAnalysisItemCard';

type TabType = 'invoices' | 'customer_insight' | 'payment_reliability_score' | 'financial_risk_score' | 'recommended_actions';

const CustomerDetails: React.FC = () => {
  const { customerId, organizationId } = useParams<{ customerId: string; organizationId: string }>();
  const navigate = useNavigate();
  const { accessToken, setUserAndToken } = useUser();
  const { setDynamicTitle } = useContext(TitleContext);

  const [customer, setCustomer] = useState<ActiveCustomer | null>(null);
  const [invoices, setInvoices] = useState<UnpaidInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoicesError, setInvoicesError] = useState('');
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<TabType>('invoices');
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState<AgentCustomerAnalysis | null>(null);
  const [selectedAnalyses, setSelectedAnalyses] = useState<{ type: string; analyses: AgentInvoiceAnalysis[] } | null>(null);
  const [showAnalysesModal, setShowAnalysesModal] = useState(false);

  // Sort invoices: latest first by invoice date, fallback to due date
  const sortedInvoices = React.useMemo(() => {
    return [...invoices].sort((a, b) => {
      const aDate = new Date(a.date_at || a.due_date_at).getTime();
      const bDate = new Date(b.date_at || b.due_date_at).getTime();
      return bDate - aDate;
    });
  }, [invoices]);

  // Fetch customer details
  const fetchCustomerDetails = useCallback(async () => {
    if (!organizationId || !customerId) return;

    try {
      setLoading(true);
      const customer = await customerService.getCustomerById(organizationId, customerId, accessToken!, setUserAndToken);
      
      if (customer) {
        setCustomer(customer);
        setDynamicTitle(`${customer.name}`);
      } else {
        setError('Customer not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch customer details');
    } finally {
      setLoading(false);
    }
  }, [organizationId, customerId, accessToken, setUserAndToken, setDynamicTitle]);

  // Fetch customer invoices
  const fetchCustomerInvoices = useCallback(async () => {
    if (!organizationId || !customer) return;

    try {
      setInvoicesLoading(true);
      const response = await customerService.getAllInvoices(organizationId, accessToken!, setUserAndToken, customer.xero_contact_id);
      setInvoices(response.invoices);
      setInvoicesError('');
    } catch (err: any) {
      setInvoicesError(err.message || 'Failed to fetch customer invoices');
    } finally {
      setInvoicesLoading(false);
    }
  }, [organizationId, customer, accessToken, setUserAndToken]);

  useEffect(() => {
    if (organizationId && customerId) {
      fetchCustomerDetails();
    }

    return () => {
      setDynamicTitle(null);
    };
  }, [organizationId, customerId, fetchCustomerDetails, setDynamicTitle]);

  useEffect(() => {
    if (customer) {
      fetchCustomerInvoices();
    }
  }, [customer, fetchCustomerInvoices]);

  // Helper function to get analyses by type
  const getAnalysesByType = (analysisType: string): AgentCustomerAnalysis[] => {
    if (!customer) return [];
    return customer.agent_analyses
      .filter(analysis => analysis.analysis_type === analysisType)
      .sort((a, b) => new Date(b.analyzed_at).getTime() - new Date(a.analyzed_at).getTime());
  };

  // Handle view analysis reasonings
  const handleViewReasonings = (analysis: AgentCustomerAnalysis) => {
    setSelectedAnalysis(analysis);
    setShowAnalysisModal(true);
  };

  // Handle view invoice analyses
  const handleViewInvoiceAnalyses = (type: string, analyses: AgentInvoiceAnalysis[]) => {
    setSelectedAnalyses({ type, analyses });
    setShowAnalysesModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading customer details...</span>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error || 'Customer not found'}
      </div>
    );
  }

  // Check which tabs have data
  const hasInvoices = invoices.length > 0;
  const hasCustomerInsight = getAnalysesByType('customer_insight').length > 0;
  const hasPaymentReliability = getAnalysesByType('payment_reliability_score').length > 0;
  const hasFinancialRisk = getAnalysesByType('financial_risk_score').length > 0;
  const hasRecommendations = getAnalysesByType('recommended_actions').length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(`/organizations/${organizationId}`)}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Organization
        </button>
      </div>

      {/* Customer Information and Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <CustomerInfoCard customer={customer} />
        </div>
        <CustomerAnalysisCard 
          customer={customer} 
          onViewReasonings={handleViewReasonings}
        />
      </div>

      {/* Tabs and Content Section */}
      <div className="bg-white rounded-lg shadow">
        <CustomerTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          hasInvoices={hasInvoices}
          hasCustomerInsight={hasCustomerInsight}
          hasPaymentReliability={hasPaymentReliability}
          hasFinancialRisk={hasFinancialRisk}
          hasRecommendations={hasRecommendations}
        />
        
        {/* Tab Content */}
        <div className="p-6">
          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <div>
              {invoicesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                  <span className="text-gray-600">Loading invoices...</span>
                </div>
              ) : invoicesError ? (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {invoicesError}
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-500 text-lg mb-2">No invoices found</div>
                  <div className="text-gray-400 text-sm">This customer has no invoices.</div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sortedInvoices.map((invoice) => (
                    <InvoiceCard
                      key={invoice.invoice_id}
                      invoice={invoice}
                      onViewAnalyses={handleViewInvoiceAnalyses}
                      organizationId={organizationId!}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Customer Insight Tab */}
          {activeTab === 'customer_insight' && (
            <div>
              {hasCustomerInsight ? (
                <div className="space-y-4">
                  {getAnalysesByType('customer_insight').map((analysis, index) => (
                    <CustomerAnalysisItemCard
                      key={analysis.id}
                      analysis={analysis}
                      index={index}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-500 text-lg mb-2">No Customer Insights Available</div>
                  <div className="text-gray-400 text-sm">Run customer analysis to generate insights.</div>
                </div>
              )}
            </div>
          )}

          {/* Payment Reliability Tab */}
          {activeTab === 'payment_reliability_score' && (
            <div>
              {hasPaymentReliability ? (
                <div className="space-y-4">
                  {getAnalysesByType('payment_reliability_score').map((analysis, index) => (
                    <CustomerAnalysisItemCard
                      key={analysis.id}
                      analysis={analysis}
                      index={index}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-500 text-lg mb-2">No Payment Reliability Assessments Available</div>
                  <div className="text-gray-400 text-sm">Run customer analysis to generate payment risk assessments.</div>
                </div>
              )}
            </div>
          )}

          {/* Financial Risk Tab */}
          {activeTab === 'financial_risk_score' && (
            <div>
              {hasFinancialRisk ? (
                <div className="space-y-4">
                  {getAnalysesByType('financial_risk_score').map((analysis, index) => (
                    <CustomerAnalysisItemCard
                      key={analysis.id}
                      analysis={analysis}
                      index={index}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-500 text-lg mb-2">No Financial Risk Assessments Available</div>
                  <div className="text-gray-400 text-sm">Run customer analysis to generate financial risk assessments.</div>
                </div>
              )}
            </div>
          )}

          {/* Recommendations Tab */}
          {activeTab === 'recommended_actions' && (
            <div>
              {hasRecommendations ? (
                <div className="space-y-4">
                  {getAnalysesByType('recommended_actions').map((analysis, index) => (
                    <CustomerAnalysisItemCard
                      key={analysis.id}
                      analysis={analysis}
                      index={index}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-500 text-lg mb-2">No Recommendations Available</div>
                  <div className="text-gray-400 text-sm">Run customer analysis to generate recommendations.</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      <ToastNotification toastMsg={toastMsg} setToastMsg={setToastMsg} />

      {/* Analysis Modal */}
      <CustomerAnalysisModal
        isOpen={showAnalysisModal}
        analysis={selectedAnalysis}
        onClose={() => {
          setShowAnalysisModal(false);
          setSelectedAnalysis(null);
        }}
      />

      {/* Invoice Analyses Modal */}
      <AnalysesModal
        isOpen={showAnalysesModal}
        analyses={selectedAnalyses}
        onClose={() => setShowAnalysesModal(false)}
      />
    </div>
  );
};

export default CustomerDetails;
